import { useEffect, useState } from 'react';

import { useMediaQuery } from './useMediaQuery';
import { DESKTOP_BREAKPOINT_QUERY } from '@/config/ui.constants';

// Below this breakpoint the sidebar is force-collapsed by default; manual
// toggles still work. Shared with `app-shell.tsx`'s mobile/desktop chrome
// branch via DESKTOP_BREAKPOINT_QUERY — they must agree on the same width.
const WIDE_VIEWPORT_QUERY = DESKTOP_BREAKPOINT_QUERY;
const STORAGE_KEY = 'sidebarCollapsed';

/**
 * Sidebar collapse state with responsive auto-collapse.
 *
 * - Below the wide breakpoint: force-collapsed by default. Manual toggle
 *   still flips state but the preference is not persisted (so an ad-hoc
 *   open on a narrow viewport doesn't overwrite the desktop default).
 * - At/above the wide breakpoint: the persisted preference is honored;
 *   manual toggle updates and persists it.
 * - Crossing the breakpoint snaps to the appropriate default (collapsed
 *   when going narrow; persisted preference when going wide).
 *
 * `startCollapsed` (DV-3364, the account detail page): at wide widths the
 * sidebar starts collapsed instead of following the stored preference, a
 * manual toggle still works but is not saved, and crossing back to wide
 * returns to collapsed. Leaving the page remounts the shell, which brings the
 * stored preference back.
 */
export function useSidebarCollapse({ startCollapsed = false }: { startCollapsed?: boolean } = {}) {
  const isWideViewport = useMediaQuery(WIDE_VIEWPORT_QUERY);
  const [collapsed, setCollapsed] = useState(() => {
    const isWide =
      typeof window === 'undefined' ||
      window.matchMedia(WIDE_VIEWPORT_QUERY).matches;
    if (!isWide) return true;
    return startCollapsed || localStorage.getItem(STORAGE_KEY) === 'true';
  });

  useEffect(() => {
    if (!isWideViewport) {
      setCollapsed(true);
    } else {
      setCollapsed(startCollapsed || localStorage.getItem(STORAGE_KEY) === 'true');
    }
  }, [isWideViewport, startCollapsed]);

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      if (isWideViewport && !startCollapsed) {
        localStorage.setItem(STORAGE_KEY, String(next));
      }
      return next;
    });
  };

  return { collapsed, toggle };
}
